import React from "react";
import { Lock } from "lucide-react";

/**
 * TierRibbon
 * -----------
 * Single source of truth for the "FREE / PREMIUM / PROFESSIONAL" tier
 * pill that appears on article surfaces (news river, front-page strip,
 * article header, coming-soon rail). Reads `article.readTier` — legacy
 * values `standard` and `free` collapse to the same "free" ribbon.
 *
 * Sizes:
 *   sm  – news-river row / front-page column (12px eyebrow)
 *   md  – article header (14px eyebrow with icon)
 *   xs  – coming-soon rail / tight grids (10px eyebrow, no icon)
 *
 * Variants:
 *   free          – neutral bordered pill, no icon (hidden by default)
 *   premium       – gold (#00B8D9) filled box with letter "P"
 *   professional  – navy (#0B1F33) filled box with label "Prof"
 * Locked audio-only variants render the same ribbon plus a lock glyph.
 */

const CONFIG = {
  professional: {
    label: "Prof",
    bg: "bg-[#0B1F33]",
    text: "text-[#FFFFFF]",
    icon: null,
    border: "border-[#0B1F33]",
  },
  premium: {
    label: "P",
    bg: "bg-[#B8E9F1]",
    text: "text-[#0B1F33]",
    icon: null,
    border: "border-[#B8E9F1]",
  },
  free: {
    label: "Free",
    bg: "bg-transparent",
    text: "text-neutral-600",
    icon: null,
    border: "border-neutral-400",
  },
};

// Compact letter-badge sizing so the ribbon sits on images without covering content.
// Padding tuned so the letter sits centered *inside* the box with breathing room.
const SIZE = {
  xxs: { pad: "px-1 py-[1px]", font: "text-[8px] tracking-normal leading-none", iconSize: "w-2 h-2" },
  xs:  { pad: "px-1.5 py-[2px]", font: "text-[9px] tracking-normal leading-none", iconSize: "w-2.5 h-2.5" },
  sm:  { pad: "px-2 py-0.5", font: "text-[10px] tracking-normal", iconSize: "w-3 h-3" },
  md:  { pad: "px-2 py-0.5", font: "text-[11px] tracking-normal", iconSize: "w-3 h-3" },
};

export function resolveTier(t) {
  const v = (t || "").toLowerCase();
  if (v === "professional" || v === "pro") return "professional";
  if (v === "premium") return "premium";
  return "free";
}

const TierRibbon = ({ tier, size = "sm", locked = false, hideFree = true, className = "" }) => {
  const key = resolveTier(tier);
  if (key === "free" && hideFree) return null;
  const cfg = CONFIG[key];
  const s = SIZE[size] || SIZE.sm;
  const Icon = cfg.icon;
  return (
    <span
      data-testid={`tier-ribbon-${key}`}
      className={`inline-flex items-center gap-1 ${cfg.bg} ${cfg.text} ${
        key === "free" ? "border " + cfg.border : ""
      } ${s.pad} ${s.font} uppercase font-condensed font-bold align-middle ${className}`}
    >
      {locked && <Lock className={s.iconSize} strokeWidth={2.4} />}
      {!locked && Icon && <Icon className={s.iconSize} strokeWidth={2.2} />}
      <span>{cfg.label}</span>
    </span>
  );
};

export default TierRibbon;
